import React, { useState, useEffect } from 'react';
import axios from '../api/axiosConfig';
import './HistorialLotesModal.css';

const HistorialLotesModal = ({ isOpen, onClose, concesionarioId, concesionarioName, onRefreshData }) => {
  const [lotes, setLotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [expandedLoteId, setExpandedLoteId] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);
  const [updatingLoteId, setUpdatingLoteId] = useState(null);

  useEffect(() => {
    if (isOpen) {
      fetchHistorial();
    }
  }, [isOpen, concesionarioId]);

  const fetchHistorial = async () => {
    try {
      setLoading(true);
      const queryParams = new URLSearchParams();
      if (concesionarioId && concesionarioId !== 'todos') {
        queryParams.append('concesionario_id', concesionarioId);
      }
      if (search) {
        queryParams.append('search', search);
      }
      const res = await axios.get(`/api/liquidacion/historial-lotes?${queryParams.toString()}`);
      setLotes(res.data || []);
    } catch (err) {
      console.error('Error al cargar historial de lotes:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleMarcarRecaudado = async (lote) => {
    let idsToUpdate = [];
    try {
      idsToUpdate = typeof lote.tramite_ids === 'string' ? JSON.parse(lote.tramite_ids) : (lote.tramite_ids || []);
    } catch (e) {
      idsToUpdate = lote.tramite_ids || [];
    }

    if (!idsToUpdate || idsToUpdate.length === 0) {
      alert('⚠️ Este registro de lote no contiene la lista de trámites asociada.');
      return;
    }

    const confirmMsg = `¿Deseas cambiar los ${idsToUpdate.length} trámites de este lote (ID #${lote.id}) al estado 'RECAUDADO'?`;
    if (!window.confirm(confirmMsg)) return;

    try {
      setUpdatingLoteId(lote.id);
      const isImpuesto = (lote.tipo_accion || '').toLowerCase() === 'impuesto_cobrado';
      const res = await axios.put(isImpuesto ? '/api/liquidacion/update-impuesto-status' : '/api/liquidacion/update-status', {
        tramite_ids: idsToUpdate,
        nuevo_estado: 'recaudado'
      });
      alert(res.data?.message || 'Trámites actualizados a RECAUDADO exitosamente.');
      await fetchHistorial();
      if (typeof onRefreshData === 'function') {
        onRefreshData();
      }
    } catch (err) {
      console.error('Error al cambiar estado de lote a recaudado:', err);
      alert('❌ Error al actualizar estado del lote: ' + (err.response?.data?.message || err.message));
    } finally {
      setUpdatingLoteId(null);
    }
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    fetchHistorial();
  };

  const handleDownload = async (lote) => {
    try {
      setDownloadingId(lote.id);
      const res = await axios.get(`/api/liquidacion/historial-lotes/${lote.id}/descargar`, {
        responseType: 'blob'
      });

      let filename = lote.archivo_nombre || `Lote_${lote.id}_${lote.tipo_accion}.zip`;
      const disposition = res.headers['content-disposition'];
      if (disposition && disposition.includes('filename=')) {
        const match = disposition.match(/filename="?([^";]+)"?/);
        if (match && match[1]) filename = match[1];
      }

      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error al descargar lote del historial:', err);
      alert('❌ Error al descargar el archivo del lote');
    } finally {
      setDownloadingId(null);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    return d.toLocaleString('es-CO', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const getBadgeStyle = (tipo) => {
    const t = (tipo || '').toLowerCase();
    if (t === 'cobrado') return { background: '#dcfce7', color: '#15803d', border: '1px solid #bbf7d0' };
    if (t === 'recaudado') return { background: '#dbeafe', color: '#1d4ed8', border: '1px solid #bfdbfe' };
    if (t === 'pagado') return { background: '#fef3c7', color: '#b45309', border: '1px solid #fde68a' };
    if (t === 'paquete_generado') return { background: '#f3e8ff', color: '#6b21a8', border: '1px solid #e9d5ff' };
    if (t === 'impuesto_cobrado') return { background: '#ffedd5', color: '#c2410c', border: '1px solid #fed7aa' };
    if (t === 'impuesto_recaudado') return { background: '#cffafe', color: '#0e7490', border: '1px solid #a5f3fc' };
    return { background: '#f1f5f9', color: '#475569', border: '1px solid #e2e8f0' };
  };

  const getBadgeLabel = (tipo) => {
    const t = (tipo || '').toUpperCase();
    if (t === 'PAQUETE_GENERADO') return '📦 PAQUETE GENERADO';
    if (t === 'COBRADO') return '✅ COBRADO EN LOTE';
    if (t === 'RECAUDADO') return '💰 RECAUDADO EN LOTE';
    if (t === 'PAGADO') return '💳 PAGADO EN LOTE';
    if (t === 'IMPUESTO_COBRADO') return '🧾 IMPUESTOS COBRADOS';
    if (t === 'IMPUESTO_RECAUDADO') return '💰 IMPUESTOS RECAUDADOS';
    return t;
  };

  if (!isOpen) return null;

  return (
    <div className="historial-modal-overlay">
      <div className="historial-modal-container">
        <div className="historial-modal-header">
          <div>
            <h2>📜 Historial de Cobros y Recaudos en Lote</h2>
            <p className="historial-subtitle">
              {concesionarioName ? `Filtrado por: ${concesionarioName}` : 'Mostrando historial de todos los concesionarios'}
            </p>
          </div>
          <button className="historial-close-btn" onClick={onClose}>✕</button>
        </div>

        <div className="historial-modal-toolbar">
          <form onSubmit={handleSearchSubmit} className="historial-search-form">
            <input
              type="text"
              placeholder="🔍 Buscar por placa, usuario o estado..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="historial-search-input"
            />
            <button type="submit" className="historial-btn-search">Buscar</button>
          </form>
          <button onClick={fetchHistorial} className="historial-btn-refresh" title="Actualizar">
            🔄 Actualizar
          </button>
        </div>

        <div className="historial-modal-body">
          {loading ? (
            <div className="historial-loading">⏳ Cargando historial de lotes...</div>
          ) : lotes.length === 0 ? (
            <div className="historial-no-data">
              📭 No hay registros de cobros o recaudos en lote para esta consulta.
            </div>
          ) : (
            <table className="historial-table">
              <thead>
                <tr>
                  <th># ID / Fecha</th>
                  <th>Concesionario</th>
                  <th>Acción / Estado</th>
                  <th>Usuario</th>
                  <th>Cant. Placas</th>
                  <th>Placas Procesadas</th>
                  <th style={{ textAlign: 'center' }}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {lotes.map((lote) => {
                  let platesArr = [];
                  try {
                    platesArr = typeof lote.placas === 'string' ? JSON.parse(lote.placas) : (lote.placas || []);
                  } catch (e) {
                    platesArr = lote.placas || [];
                  }

                  const isExpanded = expandedLoteId === lote.id;
                  const displayPlates = isExpanded ? platesArr : platesArr.slice(0, 5);
                  const hasMore = platesArr.length > 5;

                  return (
                    <tr key={lote.id}>
                      <td>
                        <strong>#{lote.id}</strong>
                        <div className="historial-date">{formatDate(lote.created_at)}</div>
                      </td>
                      <td>
                        <strong>{lote.concesionario_nombre}</strong>
                      </td>
                      <td>
                        <span className="historial-badge" style={getBadgeStyle(lote.tipo_accion)}>
                          {getBadgeLabel(lote.tipo_accion)}
                        </span>
                      </td>
                      <td>
                        <div style={{ fontWeight: '600' }}>{lote.usuario_nombre || 'Sistema'}</div>
                      </td>
                      <td>
                        <span className="historial-count-badge">
                          {lote.total_tramites || platesArr.length} Placas
                        </span>
                      </td>
                      <td>
                        <div className="historial-plates-wrapper">
                          {displayPlates.map((placa, idx) => (
                            <span key={idx} className="historial-plate-tag">
                              {placa}
                            </span>
                          ))}
                          {hasMore && (
                            <button
                              className="historial-more-btn"
                              onClick={() => setExpandedLoteId(isExpanded ? null : lote.id)}
                            >
                              {isExpanded ? 'Ver menos' : `+${platesArr.length - 5} más`}
                            </button>
                          )}
                        </div>
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <div style={{ display: 'flex', gap: '6px', justifyContent: 'center', flexWrap: 'wrap', alignItems: 'center' }}>
                          <button
                            className="historial-download-btn"
                            disabled={downloadingId === lote.id}
                            onClick={() => handleDownload(lote)}
                          >
                            {downloadingId === lote.id ? '⏳ Descargando...' : '📥 Descargar Archivo'}
                          </button>
                          {['cobrado', 'impuesto_cobrado'].includes((lote.tipo_accion || '').toLowerCase()) && (
                            <button
                              className="historial-action-btn"
                              disabled={updatingLoteId === lote.id}
                              onClick={() => handleMarcarRecaudado(lote)}
                              style={{
                                background: '#059669',
                                color: '#ffffff',
                                border: 'none',
                                padding: '6px 12px',
                                borderRadius: '6px',
                                fontSize: '11px',
                                fontWeight: '700',
                                cursor: 'pointer',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '4px',
                                boxShadow: '0 1px 3px rgba(5, 150, 105, 0.2)'
                              }}
                              title="Cambiar los trámites de este lote a estado RECAUDADO"
                            >
                              {updatingLoteId === lote.id ? '⏳ Procesando...' : '💰 Pasarlo a Recaudado'}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default HistorialLotesModal;
